import React, { ChangeEvent, CSSProperties, ReactNode, useState } from 'react'
import clsx from 'clsx'
import { FieldProps, getIn } from 'formik'
import { MenuItem, Tooltip, Box, Checkbox } from '@material-ui/core'
import LiquidSelect from '../LiquidSelect'
import { LiquidSelectProps } from '../LiquidSelect/LiquidSelect'
import LiquidFormLabel from '@src/components/LiquidFormLabel'
import LiquidFormHelpText from '@src/components/LiquidFormHelpText'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  formControl: {
    '& .MuiTypography-root': {
      width: '100%',
    },
  },
  formControlLabel: {
    '& .MuiTypography-root': {
      width: '100%',
    },
    '& .MuiSelect-root:focus': {
      backgroundColor: 'transparent',
    },
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.common.white,
      height: 40,
      '&.Mui-disabled': {
        backgroundColor: theme.palette.grey[200],
      },
    },
  },
  formLabel: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(1),
  },
}))

export interface LiquidMultiSelectAdapterProps
  extends FieldProps,
    LiquidSelectProps {
  rootClass?: string
  rootStyle?: CSSProperties
  label?: ReactNode
  required?: boolean
  help?: ReactNode
  options: { value: any; label: React.ReactNode; tooltip?: string }[]
  hasPlaceHolder?: boolean
  dataScrub?: boolean
  onChange?: (value: any) => void
}
interface MySelectDisplayProps extends React.HTMLAttributes<HTMLDivElement> {
  'data-scrub'?: string
}

const LiquidMultiSelectAdapter: React.FC<LiquidMultiSelectAdapterProps> = ({
  rootClass,
  rootStyle,
  size = 'medium',
  field,
  form,
  label,
  required,
  help,
  options,
  hasPlaceHolder = false,
  onOpen,
  onClose,
  onChange,
  dataScrub = false,
  ...otherProps
}) => {
  const error = getIn(form.errors, field.name)
  const touched = getIn(form.touched, field.name)
  const hasError = error && touched
  const [showTooltip, setShowTooltip] = useState(false)

  const classes = useStyles()

  const handleChange = (
    e: ChangeEvent<{ name?: string | undefined; value: unknown }>
  ) => {
    const { value } = e.target
    form.setFieldValue(field.name, value)
    onChange?.(value)
  }

  return (
    <div
      className={clsx({
        [String(rootClass)]: rootClass,
      })}
      style={rootStyle}
    >
      {label && (
        <LiquidFormLabel
          className={classes.formControlLabel}
          htmlFor={field.name}
          size={size}
          required={required}
          error={hasError}
        >
          {label}
        </LiquidFormLabel>
      )}
      <LiquidSelect
        id={field.name}
        {...field}
        size={size}
        multiple
        onChange={handleChange}
        renderValue={(selected: unknown) => {
          const selectedValues = Array.isArray(selected) ? selected : [selected]
          return selectedValues
            .map((val) => {
              const opt = options.find((o) => String(o.value) === String(val))
              return opt?.label ?? String(val)
            })
            .join(', ')
        }}
        value={field.value || []}
        error={Boolean(hasError)}
        aria-describedby={(hasError || help) && `${field.name}-help-text`}
        onOpen={(e) => {
          if (onOpen) onOpen(e)
          setShowTooltip(true)
        }}
        onClose={(e) => {
          if (onClose) onClose(e)
          setShowTooltip(false)
        }}
        {...otherProps}
        SelectDisplayProps={
          {
            'data-scrub': dataScrub ? 'true' : undefined,
          } as MySelectDisplayProps
        }
      >
        {options.map(({ label, value, tooltip }) => (
          <MenuItem
            key={value}
            value={value}
            disabled={value === '' && hasPlaceHolder}
            data-scrub={dataScrub ? true : undefined}
          >
            {!!tooltip ? (
              <Tooltip
                title={tooltip}
                placement="right"
                arrow
                disableHoverListener={!showTooltip}
              >
                <Box width="100%">
                  <Checkbox
                    checked={
                      Array.isArray(field.value) && field.value.includes(value)
                    }
                    color="primary"
                  />
                  {label}
                </Box>
              </Tooltip>
            ) : (
              <Box width="100%">
                <Checkbox
                  checked={
                    Array.isArray(field.value) && field.value.includes(value)
                  }
                  color="primary"
                />
                {label}
              </Box>
            )}
          </MenuItem>
        ))}
      </LiquidSelect>
      {(hasError || help) && (
        <LiquidFormHelpText
          id={`${field.name}-help-text`}
          error={Boolean(hasError)}
        >
          {hasError ? error : help ? help : null}
        </LiquidFormHelpText>
      )}
    </div>
  )
}

export default LiquidMultiSelectAdapter
